// ── NEXT 7 DAYS on the Weather page: the county's forecast, and what it means ─
//
// The forecast rows are the same 4-km grid the season reads (wny_grid_weather_daily,
// is_forecast = true, the Open-Meteo ensemble), averaged over the county's cells the
// same way: a day counts only when at least half the cells carry it. A FORECAST is
// never mixed with an observation here, in either direction: an observed row is
// not read as tomorrow, and a forecast row is never added to the season's rain.
//
// Each day is flagged for the things a farm plans around:
//   dry      under 0.10 in, after a day under 0.25 in: a likely day to get on the ground
//   heavy    1.00 in or more
//   freeze   a low of 32°F or below (hard freeze at 28°F)
//   heat     a high of 90°F or above
// These are flags on a forecast, and the page says so.

const MIN_CELL_SHARE = 0.5;
const NEXT = { DAYS: 7, DRY_IN: 0.1, DRY_PREV_IN: 0.25, HEAVY_IN: 1.0, FROST_F: 32, HARD_F: 28, HEAT_F: 90 };

const num = (v) => (v == null || v === "" ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
const cToF = (c) => (c == null ? null : c * 9 / 5 + 32);

function isoAdd(iso, n) {
  const d = new Date(iso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// The county's next DAYS days from today, forecast rows only.
// Returns [{ date, hi_f, lo_f, rain_in, cells_reporting, cells_total, counted, flags[] }].
function forecastDays({ cellIds, rows, todayIso, days = NEXT.DAYS }) {
  const cells = [...new Set((cellIds || []).map(String))];
  const need = Math.max(1, Math.ceil(cells.length * MIN_CELL_SHARE));
  const by = new Map();
  for (const r of rows || []) {
    if (!r || r.is_forecast !== true) continue;
    const c = String(r.cell_id);
    if (!cells.includes(c)) continue;
    const k = `${c}|${String(r.obs_date).slice(0, 10)}`;
    by.set(k, r);
  }
  const out = [];
  for (let i = 0; i < days; i++) {
    const date = isoAdd(todayIso, i);
    let n = 0, hi = 0, lo = 0, p = 0, pn = 0;
    for (const c of cells) {
      const r = by.get(`${c}|${date}`);
      if (!r) continue;
      const tx = num(r.tmax_c), tn = num(r.tmin_c), pr = num(r.precip_mm);
      if (tx == null || tn == null) continue;
      n++; hi += tx; lo += tn;
      if (pr != null) { p += pr; pn++; }
    }
    const counted = n >= need && pn >= need;
    const d = { date, cells_reporting: n, cells_total: cells.length, counted,
      hi_f: counted ? Math.round(cToF(hi / n)) : null,
      lo_f: counted ? Math.round(cToF(lo / n)) : null,
      rain_in: counted ? Math.round((p / pn) / 25.4 * 100) / 100 : null, flags: [] };
    out.push(d);
  }
  for (let i = 0; i < out.length; i++) {
    const d = out[i];
    if (!d.counted) continue;
    const prev = i ? out[i - 1] : null;
    if (d.rain_in >= NEXT.HEAVY_IN) d.flags.push("heavy");
    if (d.rain_in < NEXT.DRY_IN && prev && prev.counted && prev.rain_in < NEXT.DRY_PREV_IN) d.flags.push("dry");
    if (d.lo_f <= NEXT.HARD_F) d.flags.push("hard_freeze");
    else if (d.lo_f <= NEXT.FROST_F) d.flags.push("freeze");
    if (d.hi_f >= NEXT.HEAT_F) d.flags.push("heat");
  }
  return out;
}

// The week in one line's worth of numbers.
function weekSummary(days) {
  const c = (days || []).filter((d) => d.counted);
  if (!c.length) return { counted: 0, rain_in: null, dry: 0, wettest: null, coldest: null, warmest: null };
  const rain = c.reduce((a, d) => a + d.rain_in, 0);
  const pick = (f, better) => c.reduce((b, d) => (b == null || better(f(d), f(b)) ? d : b), null);
  return { counted: c.length, rain_in: Math.round(rain * 100) / 100,
    dry: c.filter((d) => d.flags.includes("dry")).length,
    wettest: pick((d) => d.rain_in, (a, b) => a > b),
    coldest: pick((d) => d.lo_f, (a, b) => a < b),
    warmest: pick((d) => d.hi_f, (a, b) => a > b) };
}

// The day's weather picture. Rain comes from the same ensemble amounts the page
// prints, so an icon never shows rain on a day that says 0.00 in, or sun on a day
// with a tenth of an inch. Chance of rain and sky cover are the hourly model's
// (GFS via Open-Meteo); without sky cover a dry day is drawn as partly cloudy,
// never as a sun nobody forecast.
//   rain     0.10 in or more
//   showers  a trace to 0.10 in, or a 50% chance or better
//   cloudy   dry, sky 70% covered or more
//   partly   dry, 30% to 70%, or sky cover unknown
//   sunny    dry, under 30%
const ICON = { RAIN_IN: 0.1, TRACE_IN: 0.01, SHOWER_POP: 50, CLOUDY: 70, SUNNY: 30 };
function dayIcon({ rain_in, popPct = null, cloudPct = null }) {
  const r = num(rain_in), pop = num(popPct), cl = num(cloudPct);
  if (r == null) return null;
  if (r >= ICON.RAIN_IN) return "rain";
  if (r >= ICON.TRACE_IN || (pop != null && pop >= ICON.SHOWER_POP)) return "showers";
  if (cl == null) return "partly";
  if (cl >= ICON.CLOUDY) return "cloudy";
  if (cl >= ICON.SUNNY) return "partly";
  return "sunny";
}

module.exports = { NEXT, MIN_CELL_SHARE, ICON, forecastDays, weekSummary, isoAdd, dayIcon };
